import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DataTable({
  testId,
  title,
  subtitle,
  fetcher,
  columns,
  emptyText = "Aucun élément pour le moment.",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetcher());
    } catch {
      toast.error("Impossible de charger les données.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-[#1E1E2A]">{title}</h2>
          {subtitle && <p className="text-sm text-[#5F6170] mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#5F6170]">
            {items.length} {items.length > 1 ? "entrées" : "entrée"}
          </span>
          <button
            onClick={load}
            data-testid={`${testId}-refresh`}
            className="inline-flex items-center gap-2 text-sm bg-white border border-[#DDD7E2] hover:border-[#C98FA3] rounded-full px-4 py-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#DDD7E2] overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#5F6170]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-[#5F6170]">
            <Inbox className="w-6 h-6 text-[#C98FA3] mx-auto mb-3" />
            {emptyText}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3EEF4] text-[#3A2E6E] text-xs uppercase tracking-[0.16em]">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-5 py-4 font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DDD7E2]">
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-[#FAF7F8]">
                    {columns.map((c) => (
                      <td key={c.key} className="px-5 py-4 align-top text-[#1E1E2A]">
                        {c.render
                          ? c.render(it)
                          : c.format === "date"
                            ? formatDate(it[c.key])
                            : it[c.key] || (
                                <span className="text-[#5F6170]">—</span>
                              )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
